import { CommonModule } from '@angular/common';
import {
  Component,
  DoCheck,
  EventEmitter,
  forwardRef,
  inject,
  Injector,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { JobTypeService } from '../job-type-service';

@Component({
  selector: 'app-select-job-type',
  imports: [CommonModule, MatFormFieldModule, MatSelectModule],
  templateUrl: './select-job-type.html',
  styleUrls: ['./select-job-type.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectJobType),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SelectJobType),
      multi: true,
    },
  ],
})
export class SelectJobType implements OnInit, DoCheck, ControlValueAccessor, Validator {
  readonly jobTypeService = inject(JobTypeService);
  private injector = inject(Injector);

  @Input() label = 'Poste';
  @Input() jobId: number | undefined;
  @Input() required = false;
  @Output() selectionChange = new EventEmitter<number | undefined>();
  public ngControl?: NgControl;
  disabled = false;

  @ViewChild(MatSelect) private readonly matSelect?: MatSelect;

  /**
   * Reflète l'état d'erreur du FormControl parent sur le mat-select interne,
   * afin que le mat-form-field passe en rouge lorsque la valeur est invalide.
   */
  readonly errorStateMatcher: ErrorStateMatcher = {
    isErrorState: () => {
      const ctrl = this.control;
      return !!(ctrl && ctrl.invalid && (ctrl.touched || ctrl.dirty));
    },
  };

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null) ?? undefined;
    if (this.ngControl) {
      this.required = this.required || this.hasRequiredValidator(this.ngControl.control!);
      try {
        this.ngControl.valueAccessor = this;
      } catch {
        console.warn(
          'Impossible de définir le valueAccessor. Assurez-vous que ce composant est utilisé dans un contexte de formulaire (FormGroup, FormControl, etc.)',
        );
      }
    }
  }

  /**
   * Le mat-select interne n'est pas lié à un NgControl, donc Angular Material ne
   * recalcule jamais son état d'erreur tout seul. On le déclenche ici pour que le
   * mat-form-field reflète l'état invalide du FormControl parent (bordure rouge).
   */
  ngDoCheck(): void {
    this.matSelect?.updateErrorState();
  }

  onChange: (value: number | undefined) => void = () => {
    // No-op
  };

  onTouched: () => void = () => {
    // No-op
  };

  writeValue(value: number | undefined): void {
    this.jobId = value;
  }

  registerOnChange(fn: (value: number | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    // Utilise soit le control passé, soit le control injecté via NgControl
    const ctrl = control ?? this.control;
    const isRequired = this.required || (ctrl ? this.hasRequiredValidator(ctrl) : false);

    if (isRequired && !this.jobId) {
      return { required: true };
    }

    return null;
  }

  private hasRequiredValidator(control: AbstractControl): boolean {
    if (!control.validator) return false;

    const validator = control.validator({} as AbstractControl);
    return !!(validator && validator['required']);
  }

  /** Retourne le control du FormGroup / FormControl auquel ce composant est lié, si présent */
  get control(): AbstractControl | null {
    return this.ngControl ? (this.ngControl.control ?? null) : null;
  }

  onSelectionChange(value: number): void {
    this.jobId = value;
    this.onChange(this.jobId);
    this.onTouched();
    this.selectionChange.emit(this.jobId);
  }
}
